import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Stack, TableCell, Tooltip } from '@mui/material';
import TopBar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import DataTable from "../../../../../../component/DataTable";
import { StatusChip, PersonCell, SearchInput, FilterSelect } from "../../../../../../component/listing";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import { getAllExpense } from '../../../../../../services/expense';
import { getEmployeeDetails } from '../../../../../../utils/getEmployeeNames';
import PendingExpenseDialog from './PendingExpenseDialog';
import { useNotify } from '../../../../../../component/NotificationProvider';
import { tokens, displayFont, chartColors, motion } from '../../../../../../theme';
import { pressable } from "../../../../../../component/pressableProps";
import ClaimDecisionPanel from "../ClaimDecisionPanel";
import { nextAfterDecision, positionOf, isDecidable } from "../claimQueue";
import { counterRowSx } from "../../../../container/frameLayout";
import {
  normaliseClaim,
  summarise,
  statusCounts,
  claimedByMonth,
  monthChange,
  categoryShare,
  bulkApprovable,
  monthKey,
  monthLabel,
  abbreviate,
  claimState,
} from "../expenseFacts";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
// The summary row sits on the page blush inside the table's own border, so its
// cells carry no hairline of their own.
const FOOT_SX = {
  borderBottom: "none",
  fontSize: "0.875rem",
  color: tokens.text,
  py: "0.875rem",
};

const days = (n) => (n == null ? "—" : `${n.toFixed(1)} days`);

const STATE_TONE = {
  // Neutral, not amber. Pending is the resting state of nearly every claim on
  // the screen; tinting it a warning colour makes the whole queue look urgent
  // and leaves nothing for the row that actually is.
  pending: "muted",
  approved: "success",
  reimbursed: "info",
  rejected: "danger",
  overPolicy: "danger",
};
const STATE_LABEL = {
  pending: "Pending",
  approved: "Approved",
  reimbursed: "Reimbursed",
  rejected: "Rejected",
  overPolicy: "Over policy",
};

/**
 * A counter.
 *
 * The label is the book's small-caps: 12px, 600, uppercase, 0.06em. That
 * treatment is what separates a counter's label from its own sub-line — at the
 * same weight and case the two run together and the number loses its caption.
 *
 * `tone` colours the number, and only three counters take one: over policy is
 * danger, missing receipts is warning, and time to reimburse is success. The
 * rest are ink, so the ones that block a reimbursement are the ones the eye
 * finds first.
 */
const TONE_INK = {
  danger: tokens.danger,
  warning: tokens.warningText,
  success: tokens.successText,
};

const Counter = ({ label, value, sub, tone, onClick, active }) => (
  <Box
    {...(onClick ? pressable(onClick) : {})}
    sx={{
      background: tokens.card,
      border: `1px solid ${active ? tokens.primary : tokens.border}`,
      boxShadow: active ? `0 0 0 1px ${tokens.primary}` : "none",
      borderRadius: "10px",
      p: "1rem 1.125rem",
      cursor: onClick ? "pointer" : "default",
      transition: `border-color ${motion.duration[1]}ms ${motion.easing.standard}`,
      "&:hover": onClick ? { borderColor: tokens.primary } : {},
    }}
  >
    <Box
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.textMuted,
        mb: "0.625rem",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: displayFont,
        fontSize: "1.375rem",
        fontWeight: 700,
        lineHeight: 1.15,
        fontVariantNumeric: "tabular-nums",
        color: TONE_INK[tone] || tokens.text,
      }}
    >
      {value}
    </Box>
    {sub != null && (
      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.25rem" }}>{sub}</Box>
    )}
  </Box>
);

const Panel = ({ title, caption, right, children }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: "20rem",
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "1.25rem",
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: "1rem" }}>
      <Box>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
          {title}
        </Box>
        {caption && (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.1875rem" }}>{caption}</Box>
        )}
      </Box>
      {right}
    </Stack>
    {children}
  </Box>
);

/**
 * Money in the chart is abbreviated, because nobody quotes a bar.
 *
 * The axis is the reason the abbreviation works: without a scale down the left
 * a bar is a shape, and the reader has to hover every column to learn anything.
 * Four gridline labels and a baseline are enough to read a value off.
 */
const AXIS_STEPS = 4;

const SpendChart = ({ bars }) => {
  const peak = Math.max(1, ...bars.map((b) => b.amount));
  // Round the top of the axis up to something quotable, so the labels are
  // "32k / 24k / 16k / 8k" rather than "31.4k / 23.6k / …".
  const magnitude = Math.pow(10, Math.floor(Math.log10(peak)));
  const top = Math.ceil(peak / (magnitude / 2)) * (magnitude / 2) || 1;
  const ticks = Array.from({ length: AXIS_STEPS + 1 }, (_, i) => (top / AXIS_STEPS) * (AXIS_STEPS - i));

  return (
    <Stack direction="row" spacing={1.5}>
      <Stack
        sx={{
          width: "2.75rem",
          flex: "none",
          height: "9.375rem",
          justifyContent: "space-between",
          alignItems: "flex-end",
          fontSize: "0.6875rem",
          color: tokens.textMuted,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {ticks.map((t) => (
          <Box key={t}>{t === 0 ? "0" : abbreviate(t)}</Box>
        ))}
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={1.75}
          sx={{ height: "9.375rem", borderBottom: `1px solid ${tokens.borderStrong}` }}
        >
          {bars.map((b, i) => {
            const last = i === bars.length - 1;
            return (
              <Tooltip key={b.key} title={`${monthLabel(b.key)} · ${money(b.amount)}`}>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: "100%",
                    gap: "0.4375rem",
                  }}
                >
                  {/* Only the month in view states its own figure; the rest are
                      shape, and eight labels would be a table drawn badly. */}
                  {last && (
                    <Box sx={{ fontSize: "0.6875rem", color: tokens.textMuted, fontVariantNumeric: "tabular-nums" }}>
                      {abbreviate(b.amount)}
                    </Box>
                  )}
                  <Box
                    sx={{
                      width: "100%",
                      height: `${Math.max(1, (b.amount / top) * 100)}%`,
                      background: last ? tokens.primary : tokens.mutedDark,
                      borderRadius: "4px 4px 0 0",
                      transition: `height ${motion.duration[3]}ms ${motion.easing.decelerate}`,
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1.75} sx={{ mt: "0.5rem" }}>
          {bars.map((b, i) => (
            <Box
              key={b.key}
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: "0.6875rem",
                color: tokens.textMuted,
                fontWeight: i === bars.length - 1 ? 600 : 400,
              }}
            >
              {b.label}
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

/**
 * Where it goes.
 *
 * Not a stacked bar with a legend under it: the book draws one row per
 * category, each with its own track, and the track is scaled against the
 * largest category rather than against the total. That is the difference
 * between "how much of the month was travel" — which the percentage column
 * already says — and "how do these categories compare", which is what a reader
 * actually scans a list of five for.
 *
 * Categories keep their palette colour between the swatch and the bar, the same
 * rule as Reporting, which is why the index is assigned once in `categoryShare`.
 */
const CAT_GRID = "1.4fr 1fr 5.625rem 3.375rem";

const CategoryBreakdown = ({ share }) => {
  const peak = Math.max(1, ...share.rows.map((c) => c.amount));
  return (
    <Box>
      {share.rows.map((c) => {
        const colour = chartColors[c.colorIndex % chartColors.length];
        return (
          <Box
            key={c.name}
            sx={{
              display: "grid",
              gridTemplateColumns: CAT_GRID,
              gap: "0.875rem",
              alignItems: "center",
              py: "0.6875rem",
              borderBottom: `1px solid ${tokens.border}`,
              fontSize: "0.875rem",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.125} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: "0.625rem",
                  height: "0.625rem",
                  borderRadius: "3px",
                  background: colour,
                  flex: "none",
                }}
              />
              <Box sx={{ color: tokens.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.name}
              </Box>
            </Stack>
            <Box sx={{ height: "0.5rem", borderRadius: "4px", background: tokens.border, overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${(c.amount / peak) * 100}%`,
                  height: "100%",
                  background: colour,
                  transition: `width ${motion.duration[3]}ms ${motion.easing.decelerate}`,
                }}
              />
            </Box>
            <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: tokens.text }}>
              {money(c.amount)}
            </Box>
            <Box
              sx={{
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                color: tokens.textMuted,
                fontSize: "0.8125rem",
              }}
            >
              {Math.round(c.share * 100)}%
            </Box>
          </Box>
        );
      })}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: CAT_GRID,
          gap: "0.875rem",
          alignItems: "center",
          pt: "0.75rem",
          fontSize: "0.875rem",
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: tokens.text }}>Total</Box>
        <Box />
        <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: tokens.text }}>
          {money(share.total)}
        </Box>
        <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.8125rem", color: tokens.text }}>
          100%
        </Box>
      </Box>
    </Box>
  );
};

const ExpenseDashboard = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [claims, setClaims] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [month, setMonth] = useState(() => monthKey(new Date().toISOString()));
  const [selected, setSelected] = useState(new Set());
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  // The claim the decision panel is showing, if any.
  const [openClaimId, setOpenClaimId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await getAllExpense();
        if (res?.success === false) throw new Error(res?.message || "Failed to load expenses");
        const list = (res.data || []).filter((item) => item.type === "ANY");
        const combined = await getEmployeeDetails(list, list.map((d) => d.submitterId), "submitterId");
        if (!active) return;
        setClaims((combined || []).map((item) => normaliseClaim(item, item.employee)));
      } catch (err) {
        if (active) {
          setFailed(true);
          notify.error(err?.message || "Failed to load expenses");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => summarise(claims, month), [claims, month]);
  const counts = useMemo(() => statusCounts(claims), [claims]);
  const bars = useMemo(() => claimedByMonth(claims, month, 8), [claims, month]);
  const change = useMemo(() => monthChange(bars), [bars]);
  const share = useMemo(() => categoryShare(claims, month), [claims, month]);
  const byId = useMemo(() => new Map(claims.map((c) => [c._id, c])), [claims]);

  // The months the data actually covers, so the picker cannot select a month
  // that was never going to have anything in it.
  const monthOptions = useMemo(() => {
    const known = new Set(claims.map((c) => c.month).filter(Boolean));
    known.add(month);
    return [...known].sort().reverse().map((k) => ({ value: k, label: monthLabel(k) }));
  }, [claims, month]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return claims.filter((c) => {
      const state = claimState(c);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "reimbursed" ? c.reimbursed : c.status === statusFilter) ||
        (statusFilter === "overPolicy" && state === "overPolicy");
      const matchesSearch =
        !q || c.employeeName.toLowerCase().includes(q) || c.category.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [claims, search, statusFilter]);

  const bulk = useMemo(() => bulkApprovable(selected, byId), [selected, byId]);

  /**
   * What the panel steps through: claims still awaiting a decision, in the
   * order the table shows them. An approved claim opened from the list is a
   * record being read, so it is not part of the run.
   */
  const decidable = useMemo(() => visible.filter(isDecidable), [visible]);

  const toggleRow = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === visible.length ? new Set() : new Set(visible.map((c) => c._id))
    );
  }, [visible]);

  const CHIPS = [
    { key: "pending", label: "Pending", n: counts.pending },
    { key: "approved", label: "Approved", n: counts.approved },
    { key: "rejected", label: "Rejected", n: counts.rejected },
    { key: "reimbursed", label: "Reimbursed", n: counts.reimbursed },
  ];

  const columns = [
    {
      key: "receipt",
      label: "Receipt",
      width: "3.75rem",
      // Visible without opening a row: the book's reason for the column. A
      // claim that needs one and has none says so in words rather than showing
      // an empty frame.
      render: (r) =>
        r.receiptUrl ? (
          <Box
            component="img"
            src={r.receiptUrl}
            alt=""
            sx={{
              width: "2.5rem",
              height: "2rem",
              objectFit: "cover",
              borderRadius: "4px",
              border: `1px solid ${tokens.border}`,
              display: "block",
            }}
          />
        ) : r.missingReceipt ? (
          // A required receipt that is absent is drawn as an empty frame rather
          // than written as a word: it sits in the same 40x32 slot as every
          // other thumbnail, so a gap in the column is visible down the page.
          <Box
            sx={{
              width: "2.5rem",
              height: "2rem",
              borderRadius: "4px",
              background: tokens.surface,
              border: `1px dashed ${tokens.dangerBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.5625rem",
              fontWeight: 600,
              color: tokens.danger,
            }}
          >
            None
          </Box>
        ) : (
          <Box
            sx={{
              width: "2.5rem",
              height: "2rem",
              borderRadius: "4px",
              background: tokens.surface,
              border: `1px solid ${tokens.border}`,
            }}
          />
        ),
    },
    {
      key: "employee",
      label: "Claimant",
      width: "22%",
      render: (r) => <PersonCell name={r.employeeName} src={r.employee?.profileImage} />,
    },
    {
      key: "category",
      label: "Category",
      cellSx: { color: tokens.textMuted },
      render: (r) =>
        r.claimType === "mileage" && r.quantity
          ? `${r.category} · ${r.quantity} km`
          : r.category,
    },
    {
      key: "date",
      label: "Submitted",
      cellSx: { color: tokens.textMuted, whiteSpace: "nowrap" },
      render: (r) => {
        const [, m, d] = String(r.submittedAt || "").slice(0, 10).split("-");
        return m ? `${Number(d)} ${monthLabel(`2000-${m}`, { short: true })}` : "—";
      },
    },
    {
      key: "amount",
      label: "Amount",
      align: "right",
      cellSx: { fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
      render: (r) => money(r.amount),
    },
    {
      key: "status",
      label: "Status",
      width: "8rem",
      render: (r) => {
        const state = claimState(r);
        return <StatusChip label={STATE_LABEL[state] || state} tone={STATE_TONE[state]} />;
      },
    },
  ];

  const headline = [
    `${money(stats.pending.amount)} pending across ${stats.pending.count} claim${stats.pending.count === 1 ? "" : "s"}`,
    stats.overPolicy.count ? `${stats.overPolicy.count} over policy` : null,
    stats.missingReceipt.count
      ? `${stats.missingReceipt.count} missing a receipt`
      : null,
  ].filter(Boolean).join(" · ");

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading={"Expenses"}
        breadcrumbs={[{ label: "Expenses", href: "" }, { label: "Dashboard", href: "" }]}
      />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "flex-start" }}
          spacing={1.5}
          sx={{ mb: "1.25rem" }}
        >
          <Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 700, color: tokens.text }}>
              Expense claims
            </Box>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>
              {loading ? " " : headline}
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <BtnSecondary to="/app/expense-policy" sx={{ borderRadius: "8px" }}>Policy</BtnSecondary>
            <BtnPrimary
              disabled={!bulk.ok}
              onClick={() => setPendingDialogOpen(true)}
              sx={{ borderRadius: "8px" }}
            >
              {bulk.count ? `Approve ${bulk.count} selected` : "Approve selected"}
            </BtnPrimary>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          sx={{ mb: "1.25rem" }}
        >
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            {CHIPS.map((c) => {
              const active = statusFilter === c.key;
              return (
                <Box
                  key={c.key}
                  {...pressable(() => setStatusFilter(active ? "all" : c.key))}
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: active ? 600 : 400,
                    cursor: "pointer",
                    px: "0.875rem",
                    py: "0.5rem",
                    // 8px, the field radius — these are filters, and the book
                    // draws them as controls rather than as status pills.
                    borderRadius: "8px",
                    color: active ? tokens.card : tokens.textMuted,
                    background: active ? tokens.primary : "transparent",
                    border: `1px solid ${active ? tokens.primary : tokens.borderStrong}`,
                    "&:hover": { background: active ? tokens.primary : tokens.surfaceSubtle },
                  }}
                >
                  {c.label} {c.n}
                </Box>
              );
            })}
          </Stack>
          <FilterSelect
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            options={monthOptions}
            inputProps={{ "aria-label": "Month" }}
          />
        </Stack>

        {/* Six counters. Money here is exact. */}
        <Box
          sx={{
            // One row of six down to 1024, then two rows of three. The
            // breakpoint ladder here was MUI's, whose `lg` is 1200 — so
            // between 1024 and 1200 the band silently became two rows.
            ...counterRowSx(6),
            mb: "1.25rem",
          }}
        >
          <Counter
            label="Pending"
            value={money(stats.pending.amount)}
            sub={`${stats.pending.count} claims across ${stats.pending.people} ${stats.pending.people === 1 ? "person" : "people"}`}
            onClick={() => setStatusFilter("pending")}
            active={statusFilter === "pending"}
          />
          <Counter
            label="Over policy"
            value={money(stats.overPolicy.amount)}
            sub={stats.overPolicy.count ? `${stats.overPolicy.count} claim${stats.overPolicy.count === 1 ? "" : "s"}, needs a decision` : "nothing over policy"}
            tone={stats.overPolicy.count > 0 ? "danger" : undefined}
            onClick={() => setStatusFilter("overPolicy")}
            active={statusFilter === "overPolicy"}
          />
          <Counter
            label="Reimbursed"
            value={money(stats.reimbursed.amount)}
            sub={`in ${monthLabel(month, { short: true })}, ${stats.reimbursed.count} claims`}
          />
          <Counter
            label="Time to reimburse"
            value={days(stats.timeToReimburse.median)}
            tone={stats.timeToReimburse.median == null ? undefined : "success"}
            sub={
              stats.timeToReimburse.previous != null
                ? `median, was ${stats.timeToReimburse.previous.toFixed(1)} in ${monthLabel(stats.timeToReimburse.previousMonth, { short: true })}`
                : "median"
            }
          />
          <Counter
            label="Average claim"
            value={stats.averageClaim == null ? "—" : money(Math.round(stats.averageClaim))}
            sub={`across ${stats.reimbursed.count} reimbursed claims`}
          />
          <Counter
            label="Missing receipts"
            value={stats.missingReceipt.count}
            sub={stats.missingReceipt.count ? "blocking reimbursement" : "none blocking"}
            tone={stats.missingReceipt.count > 0 ? "warning" : undefined}
          />
        </Box>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
          <Panel
            title="Claimed by month"
            caption="Approved and reimbursed"
            right={
              <Box sx={{ textAlign: "right" }}>
                <Box
                  sx={{
                    fontFamily: displayFont,
                    fontSize: "1.375rem",
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                    color: tokens.text,
                  }}
                >
                  {abbreviate(bars[bars.length - 1]?.amount || 0)}
                </Box>
                {change != null && (
                  // Spending less than last month is good news, and the book
                  // colours it accordingly.
                  <Box sx={{ fontSize: "0.75rem", color: change < 0 ? tokens.successText : tokens.textMuted }}>
                    {Math.abs(Math.round(change * 100))}% {change < 0 ? "below" : "above"}{" "}
                    {monthLabel(bars[bars.length - 2]?.key, { short: true })}
                  </Box>
                )}
              </Box>
            }
          >
            <SpendChart bars={bars} />
          </Panel>

          <Panel title="Where it goes" caption={`${monthLabel(month)}, all departments`}>
            {share.rows.length ? (
              <CategoryBreakdown share={share} />
            ) : (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                Nothing approved in {monthLabel(month)} yet.
              </Box>
            )}
          </Panel>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "center" }}
          spacing={2}
          sx={{ mb: "1rem" }}
        >
          <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
            Waiting on you
          </Box>
          {/* The rule carries the heading across to the control, so the queue
              reads as one band rather than as a title floating over a table. */}
          <Box sx={{ flex: 1, height: "1px", background: tokens.border, display: { xs: "none", md: "block" } }} />
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search claimant or category"
          />
        </Stack>

        <DataTable
          rows={visible}
          loading={loading}
          error={failed}
          onRetry={() => setReloadKey((k) => k + 1)}
          errorTitle="Couldn't load expense claims"
          empty={{
            title: "No expense claims",
            description: "Nothing matches the current filter.",
          }}
          /* The panel opens over the queue and keeps it behind, so deciding
             does not cost a round trip through the list. */
          onRowClick={(r) => setOpenClaimId(r._id)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={columns}
          selected={selected}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          /**
           * The one place in PayLoom where a row tints its whole background.
           * An over-policy claim cannot be part of a bulk approval and has to be
           * opened and decided on its own, so it is marked as a row rather than
           * as a chip inside one.
           */
          rowSx={(r) =>
            claimState(r) === "overPolicy"
              ? { background: tokens.dangerTint }
              : {}
          }
          footer={
            selected.size > 0 ? (
              <>
                <TableCell sx={FOOT_SX} />
                <TableCell sx={FOOT_SX} />
                <TableCell sx={{ ...FOOT_SX, fontWeight: 600 }}>{bulk.count} selected</TableCell>
                <TableCell sx={FOOT_SX} colSpan={2}>
                  {!bulk.ok && (
                    <Box sx={{ fontSize: "0.75rem", fontWeight: 400, color: tokens.danger }}>
                      {bulk.blocked.some((c) => c.overPolicy)
                        ? "An over-policy claim has to be decided on its own"
                        : "Only pending claims can be approved together"}
                    </Box>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    ...FOOT_SX,
                    fontFamily: displayFont,
                    fontSize: "1.0625rem",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {money(bulk.amount)}
                </TableCell>
                <TableCell sx={FOOT_SX} />
              </>
            ) : null
          }
        />

        {/* What the filter is a slice of. Stated whether or not anything is
            selected, because it is the thing the queue is scoped to. */}
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "1.125rem" }}>
          All {counts.pending} pending claim{counts.pending === 1 ? "" : "s"} in {monthLabel(month)}
        </Box>
      </MainBox>

      <ClaimDecisionPanel
        open={Boolean(openClaimId)}
        claim={byId.get(openClaimId) || null}
        money={money}
        position={positionOf(decidable, openClaimId)}
        total={decidable.length}
        onClose={() => setOpenClaimId(null)}
        onDecided={(decidedId) => {
          /*
            Advance rather than close. The queue we advance through is the
            pending one, not the filtered view: a decision made while looking at
            "Approved" should still move to the next thing needing a decision.
          */
          const next = nextAfterDecision(decidable, decidable, decidedId);
          setOpenClaimId(next ? next._id : null);
          setReloadKey((k) => k + 1);
        }}
      />

      <PendingExpenseDialog
        open={pendingDialogOpen}
        onClose={() => setPendingDialogOpen(false)}
        data={[...selected].map((id) => byId.get(id)).filter(Boolean)}
        pendingAmount={bulk.amount}
      />
    </Box>
  );
};

export default ExpenseDashboard;
